import Flex from 'frontend/commons/Flex';
import { FALLBACK_IMAGE } from 'frontend/constants';
import { usePackageCard } from 'frontend/contexts/PackageCardContext';
import getFinalPrice from 'frontend/utils/get-final-price';
import { useMemo, useState } from 'react';
import { isClient } from 'frontend/helpers/ssr-helper';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DATE_KEYS } from 'frontend/constants';
import CardGallery from './components/CardGallery';
import DynamicContent from './components/DynamicContent';
import IncludesRow from './includesRow';
import DynamicIncludesRow from './DynamicIncludesRow';
import totalNightTextSetter from 'frontend/utils/total-night-text';
import store from 'storeClient';
import './PackageCard.styles.css';

const PackageCard = () => {
  const { t } = useTranslation(['layout']);
  const [showGallery, setShowGallery] = useState();
  const { packageData, displayInfo, handleDisplayInfoToggle } = usePackageCard();
  const { web } = store.getState().applicationState;

  const isInternal = useMemo(() => packageData?.isInternal, [packageData]);
  const url = web === 'localhost' ? `http://localhost:${process.env.PORT}` : `https://${web}`;

  const salidaId = packageData?.cheapestOption?.flight?.tripInformation?.[0]?.departureReg;
  const urlSalidas = `${url}/paquetes?id=${packageData.externalId}&salidaId=${salidaId}`;

  const {
    title,
    images,
    totalNights,
    packageOrigins,
    packageDestinations,
    currencyCode,
    cheapestOption,
  } = packageData;

  const departureDate = useMemo(() => {
    const tripInformation = cheapestOption?.flight?.tripInformation;
    if (tripInformation && tripInformation.length > 0) {
      const sortedTripInfo = [...tripInformation].sort((a, b) => a.id - b.id);
      const flightDepartureDate = sortedTripInfo[0]?.departureDate;
      if (flightDepartureDate) {
        return moment(flightDepartureDate).utc().format(DATE_KEYS.FORMAT.NUMBER_MONTHNAME_YEAR);
      }
    }
    return null;
  }, [cheapestOption]);

  const galleyOfImages = useMemo(() => {
    if (images && images.length > 0) {
      return images;
    }

    if (packageDestinations && packageDestinations.length > 0) {
      const destinationImages = packageDestinations
        .map((dest) => dest.destinationCity?.imageUrl)
        .filter((imageUrl) => imageUrl && imageUrl.trim() !== '');

      if (destinationImages.length > 0) {
        return destinationImages;
      }
    }

    return [FALLBACK_IMAGE];
  }, [images, packageDestinations]);

  const mainImage = useMemo(() => {
    if (images && images.length > 0) {
      return images[0];
    }

    if (packageDestinations && packageDestinations.length > 0) {
      const firstDestinationImage = packageDestinations[0]?.destinationCity?.imageUrl;
      if (firstDestinationImage && firstDestinationImage.trim() !== '') {
        return firstDestinationImage;
      }
    }

    return FALLBACK_IMAGE;
  }, [images, packageDestinations]);

  const handleShowGallery = () => {
    setShowGallery((state) => !state);
  };

  return (
    <Flex vertical className="package-card">
      <Flex className="package-card__data">
        <img
          className="package-card__data__img"
          onClick={handleShowGallery}
          src={mainImage}
          alt="Package Destination"
        />
        <Flex vertical className="package-card__data__wrapper">
          <Flex vertical className="package-card__data__wrapper__info">
            <h3 className="package-card__data__wrapper__info__title">{title}</h3>
            <div className="package-card__data__wrapper__info__details">
              <p>
                <strong>{t('packages.details.departure')}:</strong>
                <span>{packageOrigins[0].originExporta.nombreCiudad}</span>
              </p>
              <p>
                <strong>{t('searcher.destination')}:</strong>
                <span>{packageDestinations[0].destinationCity.nombreCiudad}</span>
              </p>
              <p>
                <strong>{totalNightTextSetter(totalNights)}</strong>
              </p>
              {departureDate && (
                <p className="package-card__data__wrapper__info__details__departure">
                  <strong>Salidas desde:</strong>
                  <span>{departureDate}</span>
                </p>
              )}
              <p className="package-card__data__wrapper__info__details__price">
                <strong>{t('common.since')}:</strong>
                <span>
                  {getFinalPrice({
                    currencyCode,
                    total: cheapestOption.totalManager.total,
                  })}
                </span>
              </p>
            </div>
          </Flex>
          <Flex className="package-card__data__wrapper__footer">
            {isInternal ? (
              <DynamicIncludesRow paquetesIncluidos={packageData?.icons} fullWidth />
            ) : (
              <IncludesRow />
            )}
            <Flex className="package-card__data__wrapper__footer__buttons">
              <button
                className={displayInfo ? 'active' : undefined}
                onClick={() => {
                  if (isInternal) {
                    isClient && window.open(urlSalidas, '_blank');
                  } else {
                    handleDisplayInfoToggle();
                  }
                }}
              >
                {t('common.more_details')}
              </button>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <DynamicContent />
      <CardGallery
        showGallery={showGallery}
        handleShowGallery={handleShowGallery}
        packageDestinations={packageDestinations}
        images={galleyOfImages}
      />
    </Flex>
  );
};

export default PackageCard;
